// cspell:ignore jollyplant westeurope

export type Environment = {
  /** The current environment. */
  name?: 'development' | 'production' | 'test';

  /** Whether the current environment is development. */
  development: boolean;

  /** Whether the current environment is production. */
  production: boolean;

  /** Whether the current environment is test. */
  test: boolean;

  /** The current platform. */
  platform: 'azure_app_service' | 'azure_container_apps' | 'azure_static_web_apps' | 'vercel' | undefined;

  /** Whether the app is running self-hosted on Azure. */
  azure: boolean;

  /** The current commit SHA. */
  sha?: string;

  /** When the current commit was made, in Unix seconds. */
  timestamp?: number;

  /** The current branch name. */
  branch?: string;

  /** Whether the current branch is the main branch. */
  main: boolean;
};

/**
 * Reads the environment, falling back to git for the commit. `process.getBuiltinModule` is absent
 * in the browser, where `process` is a handful of values baked in at build time, so a build that
 * needs the commit there passes its own `exec` and bakes the result in; see the vite configs.
 */
export function getEnvironment(): Environment {
  function exec(command: string): string | undefined {
    try {
      const { execSync } = process.getBuiltinModule('node:child_process');
      return execSync(command).toString().trim();
    } catch {
      return undefined;
    }
  }

  const env = process.env.NODE_ENV as Environment['name'];
  const branch =
    process.env.GITHUB_REF_NAME || process.env.VERCEL_GIT_COMMIT_REF || exec('git rev-parse --abbrev-ref HEAD');
  const sha = process.env.GITHUB_SHA || process.env.VERCEL_GIT_COMMIT_SHA || exec('git rev-parse HEAD');
  const timestamp = process.env.GIT_COMMIT_TIMESTAMP || exec('git show -s --format=%ct HEAD');
  const platform =
    (process.env.CONTAINER_APP_ENV_DNS_SUFFIX && 'azure_container_apps') ||
    (process.env.WEBSITE_HOSTNAME && 'azure_app_service') ||
    (process.env.WEBSITE_AUTH_V2_CONFIG_JSON && 'azure_static_web_apps') ||
    (process.env.VERCEL_BRANCH_URL && 'vercel') ||
    undefined;

  return {
    name: env,
    development: env === 'development',
    production: env === 'production',
    test: env === 'test',
    platform,
    azure: platform?.startsWith('azure') ?? false,
    sha,
    timestamp: timestamp ? Number(timestamp) : undefined,
    branch,
    main: branch === 'main',
  };
}

export const environment = getEnvironment();

export interface SiteUrlOptions {
  /** The default URL to use if no other URL is found. */
  defaultValue: string;
}

export function getSiteUrl({ defaultValue }: SiteUrlOptions): string {
  const { development, main } = environment;

  // if we are in development, use portless or localhost
  if (development) return process.env.PORTLESS_URL || `http://localhost:${process.env.PORT || 3000}`;

  // if we are on the main branch, use the known URL
  if (main) return defaultValue;

  // if we are on Azure ContainerApps, use the provided URL
  let value = getSiteUrlForAca();
  if (value && value.length > 0) return value;

  // if we are on Azure App Service, use the provided URL
  value = getSiteUrlForAppService();
  if (value && value.length > 0) return value;

  // if we are on Azure Static WebApps, use the provided URL
  value = getSiteUrlForSwa();
  if (value && value.length > 0) return value;

  // if we are on Vercel, use the provided URL
  value = process.env.VERCEL_BRANCH_URL;
  if (value && value.length > 0) return `https://${value}`;

  return defaultValue; // fallback (edge cases)
}

function getSiteUrlForAca(): string | undefined {
  /*
   * Having looked at the available ENV variables when deployed, we can form the URL from
   * combinations of the following variables:
   * CONTAINER_APP_ENV_DNS_SUFFIX (e.g. "jollyplant-9349db20.uksouth.azurecontainerapps.io")
   * CONTAINER_APP_NAME (e.g. "awpmarine-website")
   */

  const suffix = process.env.CONTAINER_APP_ENV_DNS_SUFFIX;
  const name = process.env.CONTAINER_APP_NAME;
  if (!suffix || !name) return undefined;
  return `https://${name}.${suffix}`;
}

function getSiteUrlForAppService(): string | undefined {
  /*
   * Environment variables for Azure App Service are documented at
   * https://learn.microsoft.com/en-us/azure/app-service/reference-app-settings?tabs=kudu%2Cdotnet#app-environment
   *
   * WEBSITE_HOSTNAME (e.g. "frontend-website.azurewebsites.net")
   */

  const value = process.env.WEBSITE_HOSTNAME;
  return value ? `https://${value}` : undefined;
}

export function getSiteUrlForSwa(): string | undefined {
  /*
   * Having looked at the available ENV variables when deployed to both production and preview environments,
   * only the WEBSITE_AUTH_V2_CONFIG_JSON has values we can use for this.
   *
   * Sample value for production:
   * {\"platform\":{\"enabled\":true},\"globalValidation\":{\"excludedPaths\":[\"/.swa/health.html\"]},\"identityProviders\":{\"azureStaticWebApps\":{\"registration\":{\"clientId\":\"black-bush-020715303.5.azurestaticapps.net\"}}},\"legacyProperties\":{\"configVersion\":\"v2\",\"legacyVersion\":\"V2\"}}
   *
   * Sample value for preview environment (named 331):
   * {\"platform\":{\"enabled\":true},\"globalValidation\":{\"excludedPaths\":[\"/.swa/health.html\"]},\"identityProviders\":{\"azureStaticWebApps\":{\"registration\":{\"clientId\":\"black-bush-020715303-331.westeurope.5.azurestaticapps.net\"}}},\"legacyProperties\":{\"configVersion\":\"v2\",\"legacyVersion\":\"V2\"}}
   *
   * The part we are interested in is the clientId value. We can extract this value and use it as the domain to form the siteUrl.
   */

  const config = process.env.WEBSITE_AUTH_V2_CONFIG_JSON;
  const clientIdMatch = config?.match(/"clientId":"([^"]+)"/);
  return clientIdMatch ? `https://${clientIdMatch[1]}` : undefined;
}
